"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  IconButton,
  Tooltip,
  Grid,
  AppBar,
  Toolbar,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Snackbar,
  Alert,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  Chip,
  Card,
  CardContent,
  Pagination,
  Tabs,
  Tab,
  alpha,
  InputAdornment,
  TablePagination,
  LinearProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Work as WorkIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import axios from "axios";
 import { useHistory } from "react-router";
import { IonPage } from "@ionic/react";
import { requestID, baseUrl, config, getBusinessData } from "../../../common/utils";

// --- Constants and Types ---
type Job = {
  job_id?: string;
  title: string;
  job_type: string;
  date_created: string;
  expiration_date: string;
  status?: string;
  category?: string;
  summary?: string;
  responsibilities?: string;
  requirements?: string;
  qualifications?: string;
  benefits?: string;
  min_salary?: string;
  max_salary?: string;
  company_name?: string;
  company_description?: string;
  website?: string;
  application_email?: string;
  description?: string;
  branch_id?: string;
  business_id?: string;
  batch_id?: string;
};

interface JobTitle {
  id: number;
  title: string;
}

const jobTypes = [
  "Full-Time",
  "Part-Time",
  "Contract",
  "Internship",
  "Temporary",
];

const statusOptions = ["Active", "Draft", "Expired", "Closed"];

const batchOptions = [
  { label: "A", id: "batch_001" },
  { label: "B", id: "batch_002" },
  { label: "C", id: "batch_003" },
];

const today = new Date().toISOString().split("T")[0];
const businessdata = getBusinessData() ??  null

// console.log({businessdata})
// console.log(businessdata.business_id, businessdata.branch_id);

// --- JobFormDialog Component ---
interface JobFormDialogProps {
  open: boolean;
  onClose: () => void;
  job?: Job | null;
  onSave: (job: Job) => void;
  loading: boolean;
  jobTitles: JobTitle[];
}

const JobFormDialog: React.FC<JobFormDialogProps> = ({
  open,
  onClose,
  job,
  onSave,
  loading,
  jobTitles,
}) => {
  const [formValues, setFormValues] = useState<Job>(
    job || {
      title: "",
      job_type: "",
      date_created: today,
      expiration_date: "",
      summary: "",
      responsibilities: "",
      requirements: "",
      qualifications: "",
      benefits: "",
      min_salary: "",
      max_salary: "",
      company_name: "",
      company_description: "",
      website: "",
      application_email: "",
      description: "",
      branch_id: businessdata.branch_id,
      business_id: businessdata.business_id,
      batch_id: "",
    }
  );

  useEffect(() => {
    setFormValues(
      job || {
        title: "",
        job_type: "",
        date_created: today,
        expiration_date: "",
        summary: "",
        responsibilities: "",
        requirements: "",
        qualifications: "",
        benefits: "",
        min_salary: "",
        max_salary: "",
        company_name: "",
        company_description: "",
        website: "",
        application_email: "",
        description: "",
        branch_id: businessdata?.branch_id,
        business_id: businessdata?.business_id,
        batch_id: "",
      }
    );
  }, [job]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSave(formValues);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {job ? "Edit Job" : "Add New Job"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3, bgcolor: "grey.50" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="job-title-label">Job Title</InputLabel>
              <Select
                labelId="job-title-label"
                id="job-title"
                name="title"
                value={formValues.title}
                onChange={handleChange}
                label="Job Title"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select Job Title</em>
                </MenuItem>
                {jobTitles.map((jt) => (
                  <MenuItem key={jt.id} value={jt.title}>
                    {jt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                name="job_type"
                value={formValues.job_type}
                onChange={handleChange}
                label="Job Type"
              >
                {jobTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Category"
              name="category"
              fullWidth
              value={formValues.category}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Batch</InputLabel>
              <Select
                name="batch_id"
                value={formValues.batch_id}
                onChange={handleChange}
                label="Batch"
              >
                {batchOptions.map((batch) => (
                  <MenuItem key={batch.id} value={batch.id}>
                    {batch.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Job Summary"
              name="summary"
              multiline
              rows={2}
              fullWidth
              value={formValues.summary}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Responsibilities"
              name="responsibilities"
              multiline
              rows={3}
              fullWidth
              value={formValues.responsibilities}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Requirements"
              name="requirements"
              multiline
              rows={3}
              fullWidth
              value={formValues.requirements}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Preferred Qualifications"
              name="qualifications"
              multiline
              rows={2}
              fullWidth
              value={formValues.qualifications}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Full Job Description"
              name="description"
              multiline
              rows={4}
              fullWidth
              value={formValues.description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Benefits"
              name="benefits"
              multiline
              rows={2}
              fullWidth
              value={formValues.benefits}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Salary Min"
              name="min_salary"
              fullWidth
              value={formValues.min_salary}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Salary Max"
              name="max_salary"
              fullWidth
              value={formValues.max_salary}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Name"
              name="company_name"
              fullWidth
              value={formValues.company_name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Website"
              name="website"
              fullWidth
              value={formValues.website}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Company Description"
              name="company_description"
              multiline
              rows={3}
              fullWidth
              value={formValues.company_description}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Date Created"
              name="date_created"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formValues.date_created}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Application Deadline"
              name="expiration_date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formValues.expiration_date}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Application Email"
              name="application_email"
              fullWidth
              value={formValues.application_email}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          startIcon={<CancelIcon />}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            loading ||
            !formValues.title ||
            !formValues.job_type ||
            !formValues.expiration_date
          }
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          sx={{ borderRadius: 2 }}
        >
          {job ? "Update Job" : "Publish Job"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main JobManagementPage Component ---
const JobManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const router = useHistory();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Add/Edit Job Dialog state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [jobToEdit, setJobToEdit] = useState<Job | null>(null);
  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);

  const [filters, setFilters] = useState({
    title: "",
    expiration_date: "",
    job_type: "",
    date_created: "",
    status: "",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}/jobs/getAllJobs`,
        payload,
        config
      );
      console.log({res})
      setJobs(res.data.data);
    } catch (err) {
      showSnackbar("Failed to fetch jobs.", "error");
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobTitles = async () => {
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}/jobs/getJobTitles`,
        payload,
        config
      );
      setJobTitles(res.data.data);
    } catch (err: any) {
      showSnackbar("Failed to fetch job titles.", "error");
      console.error("Fetch job titles error:", err);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchJobTitles();
  }, []);

  useEffect(() => {
    let result = jobs?.filter(
      (job) =>
        (filters.title
          ? job.title.toLowerCase().includes(filters.title.toLowerCase())
          : true) &&
        (filters.date_created
          ? job.date_created.includes(filters.date_created)
          : true) &&
        (filters.expiration_date
          ? job.expiration_date.includes(filters.expiration_date)
          : true) &&
        (filters.job_type
          ? job.job_type.toLowerCase().includes(filters.job_type.toLowerCase())
          : true) &&
        (filters.status
          ? job.status?.toLowerCase().includes(filters.status.toLowerCase())
          : true)
    );

    // Filter by tab
    if (activeTab === 1) {
      result = result.filter((job) => job.status === "Active");
    } else if (activeTab === 2) {
      result = result.filter((job) => job.status === "Draft");
    } else if (activeTab === 3) {
      result = result.filter((job) => job.status === "Expired");
    }

    setFilteredJobs(result);
  }, [filters, jobs, activeTab]);

  const handleFilterChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilters({ ...filters, [field]: e.target.value });
      setPage(0); // Reset to first page when filtering
    };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (job: Job) => {
    if (job.job_id) {
      router.push(`/folder/jobs/${job.job_id}`);
    } else {
      showSnackbar("Job ID not available for details.", "error");
    }
  };

  const handleAddJob = () => {
    setJobToEdit(null);
    setShowFormDialog(true);
  };

  const handleEditJob = (job: Job) => {
    setJobToEdit(job);
    setShowFormDialog(true);
  };

  const handleSaveJob = async (jobData: Job) => {
    setActionLoading("save");
    try {
      const payload = {
        requestID: requestID(),
        ...jobData,
      };

      if (jobToEdit) {
        await axios.post(
          `${baseUrl()}/jobs/editJob`,
          { ...payload, job_id: jobToEdit.job_id },
          config
        );
        showSnackbar("Job updated successfully!", "success");
      } else {
       const response =  await axios.post(`${baseUrl()}/jobs/addJob`, payload, config);
       console.log({response});
        showSnackbar("Job added successfully!", "success");
      }
      fetchJobs();
      setShowFormDialog(false);
    } catch (err) {
      showSnackbar("Failed to save job.", "error");
      console.error("Save job error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteJob = async (jobId?: string) => {
    if (!jobId) return;
    if (!window.confirm("Are you sure you want to delete this job?")) return;

    setActionLoading(jobId);
    try {
      await axios.post(
        `${baseUrl()}/jobs/deleteJob`,
        { requestID: requestID(), job_id: jobId },
        config
      );
      showSnackbar("Job deleted successfully!", "success");
      fetchJobs();
    } catch (err) {
      showSnackbar("Failed to delete job.", "error");
      console.error("Delete job error:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "warning";
      case "expired":
        return "error";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredJobs.length) : 0;

  return (
    <IonPage style={{ overflow: "scroll" }} className="pushDown">
      <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
        {/* Header */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: "white",
            color: "text.primary",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Toolbar>
            <Avatar
              sx={{
                mr: 2,
                bgcolor: "primary.main",
                width: 40,
                height: 40,
              }}
            >
              <WorkIcon />
            </Avatar>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Job Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddJob}
              sx={{
                ml: 2,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              New Job
            </Button>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Card
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: 3,
                      boxShadow: 3,
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <BusinessIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {jobs?.length}
                      </Typography>
                      <Typography variant="body2">Total Jobs</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card
                    sx={{
                      bgcolor: "success.main",
                      color: "white",
                      borderRadius: 3,
                      boxShadow: 3,
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <TimeIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {jobs?.filter((j) => j.status === "Active")?.length}
                      </Typography>
                      <Typography variant="body2">Active Jobs</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card
                    sx={{
                      bgcolor: "warning.main",
                      color: "white",
                      borderRadius: 3,
                      boxShadow: 3,
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <CalendarIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {jobs?.filter((j) => j.status === "Draft")?.length}
                      </Typography>
                      <Typography variant="body2">Drafts</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card
                    sx={{
                      bgcolor: "error.main",
                      color: "white",
                      borderRadius: 3,
                      boxShadow: 3,
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <CloseIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {jobs?.filter((j) => j.status === "Expired")?.length}
                      </Typography>
                      <Typography variant="body2">Expired</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "white",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Job Listings
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetchJobs}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  >
                    Refresh
                  </Button>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                  <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="All Jobs" />
                    <Tab label="Active" />
                    <Tab label="Drafts" />
                    <Tab label="Expired" />
                  </Tabs>
                </Box>

                {/* Filters */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      placeholder="Search jobs..."
                      value={filters.title}
                      onChange={handleFilterChange("title")}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 2 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Job Type</InputLabel>
                      <Select
                        value={filters.job_type}
                        onChange={(e) =>
                          setFilters({ ...filters, job_type: e.target.value })
                        }
                        label="Job Type"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {jobTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        label="Status"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">All Status</MenuItem>
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="From Date"
                      value={filters.date_created}
                      onChange={handleFilterChange("date_created")}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      type="date"
                      label="To Date"
                      value={filters.expiration_date}
                      onChange={handleFilterChange("expiration_date")}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                </Grid>

                {loading ? (
                  <Box sx={{ width: "100%", py: 4 }}>
                    <LinearProgress />
                    <Typography
                      variant="body1"
                      textAlign="center"
                      sx={{ mt: 2 }}
                    >
                      Loading jobs...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        overflow: "auto",
                      }}
                    >
                      <Table>
                        <TableHead sx={{ bgcolor: "grey.50" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Job Title
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Created
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Deadline
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", py: 2 }}>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredJobs?.length > 0 ? (
                            filteredJobs
                              .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                              )
                              .map((job, idx) => (
                                <TableRow
                                  key={job.job_id || idx}
                                  hover
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.05
                                      ),
                                    },
                                  }}
                                >
                                  <TableCell sx={{ py: 2 }}>
                                    <Typography
                                      variant="body1"
                                      fontWeight="medium"
                                    >
                                      {job.title}
                                    </Typography>
                                    {job.category && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {job.category}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ py: 2 }}>
                                    <Chip
                                      label={job.job_type}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell sx={{ py: 2 }}>
                                    <Chip
                                      label={job.status || "Unknown"}
                                      size="small"
                                      color={getStatusColor(job.status) as any}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ py: 2 }}>
                                    <Typography variant="body2">
                                      {new Date(
                                        job.date_created
                                      ).toLocaleDateString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 2 }}>
                                    <Typography
                                      variant="body2"
                                      color={
                                        new Date(job.expiration_date) <
                                        new Date()
                                          ? "error"
                                          : "text.primary"
                                      }
                                    >
                                      {new Date(
                                        job.expiration_date
                                      ).toLocaleDateString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ py: 2 }}>
                                    <Stack direction="row" spacing={1}>
                                      <Tooltip title="View Details">
                                        <IconButton
                                          onClick={() => handleViewDetails(job)}
                                          color="primary"
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.primary.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={() => handleEditJob(job)}
                                          color="info"
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.info.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.info.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          onClick={() =>
                                            handleDeleteJob(job.job_id)
                                          }
                                          color="error"
                                          size="small"
                                          disabled={
                                            actionLoading === job.job_id
                                          }
                                          sx={{
                                            bgcolor: alpha(
                                              theme.palette.error.main,
                                              0.1
                                            ),
                                            "&:hover": {
                                              bgcolor: alpha(
                                                theme.palette.error.main,
                                                0.2
                                              ),
                                            },
                                          }}
                                        >
                                          {actionLoading === job.job_id ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <DeleteIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{ py: 4 }}
                              >
                                <Typography variant="h6" color="text.secondary">
                                  No jobs found
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Try adjusting your filters or add a new job.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                              <TableCell colSpan={6} />
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {filteredJobs?.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {page * rowsPerPage + 1} to{" "}
                          {Math.min(
                            (page + 1) * rowsPerPage,
                            filteredJobs.length
                          )}{" "}
                          of {filteredJobs?.length} entries
                        </Typography>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          component="div"
                          count={filteredJobs?.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Add/Edit Job Form Dialog */}
          <JobFormDialog
            open={showFormDialog}
            onClose={() => setShowFormDialog(false)}
            job={jobToEdit}
            onSave={handleSaveJob}
            loading={actionLoading === "save"}
            jobTitles={jobTitles}
          />

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%", borderRadius: 2 }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </IonPage>
  );
};

export default JobManagementPage;
